import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';

export type VendorStatus = 'PENDING' | 'ACTIVE' | 'INACTIVE';
export type VendorDocType =
  | 'QUOTE'
  | 'WCB'
  | 'LIABILITY_INSURANCE'
  | 'GENERAL'
  | 'RATE_AGREEMENT';
export type ResourceType = 'EQUIPMENT' | 'SUBCONTRACTOR' | 'MATERIAL' | 'ATTACHMENT' | 'BUNDLE';
export type RateStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUPERSEDED';

/** V2-078: one compliance document a vendor needs, as the Vendor Compliance report lists it. */
export type ComplianceDocType = 'WCB' | 'LIABILITY_INSURANCE' | 'VEHICLE_INSURANCE';
export type ComplianceRowStatus = 'OVERDUE' | 'DUE_SOON' | 'MISSING' | 'CURRENT';

export interface VendorComplianceRow {
  vendorId: number;
  vendorName: string;
  vendorStatus: VendorStatus;
  resourceId: number | null;
  resourceName: string | null;
  resourceActive: boolean | null;
  docType: ComplianceDocType;
  /** ISO string over the wire; null when there is no date (missing, or an undated WCB). */
  expiresAt: string | null;
  status: ComplianceRowStatus;
  serviceAreas: Array<{ id: number; name: string }>;
}

export interface VendorComplianceCounts {
  overdue: number;
  dueSoon: number;
  missing: number;
}

export interface VendorComplianceFilters {
  status?: ComplianceRowStatus[];
  docType?: ComplianceDocType[];
  serviceAreaIds?: number[];
  vendorIds?: number[];
  includeInactive?: boolean;
}

/** Query string for the compliance report, shared by the JSON call and the CSV link. */
export function vendorComplianceQuery(filters: VendorComplianceFilters = {}): string {
  const q = new URLSearchParams();
  if (filters.status?.length) q.set('status', filters.status.join(','));
  if (filters.docType?.length) q.set('docType', filters.docType.join(','));
  if (filters.serviceAreaIds?.length) q.set('serviceAreaIds', filters.serviceAreaIds.join(','));
  if (filters.vendorIds?.length) q.set('vendorIds', filters.vendorIds.join(','));
  if (filters.includeInactive) q.set('includeInactive', 'true');
  return q.toString();
}

export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  EQUIPMENT: 'Hired Equipment (Blue Book)',
  SUBCONTRACTOR: 'Subcontractor',
  MATERIAL: 'Material',
  ATTACHMENT: 'Attachment',
  BUNDLE: 'Bundle',
};

/**
 * The heading a resource is listed under. The same machine is deliberately entered twice - once
 * as EQUIPMENT (blue book) and once as SUBCONTRACTOR - because the two carry different rates,
 * so the type is what separates them. Attachments have their own heading (M1): they share the
 * hired-equipment markup bucket, but listing them under the blue book made the equipment
 * count read high and hid how many attachments a vendor actually has.
 *
 * The order matches the server's `sortBy=group` (the ResourceType enum order), so a paged
 * catalogue renders its headings in the same sequence the rows arrive in.
 *
 * One helper rather than a switch per list: the vendor detail table, the admin table and any
 * future resource picker must agree on the headings, or the same resource appears under
 * different names depending on where you looked at it.
 */
export const RESOURCE_GROUP_ORDER = [
  'Hired Equipment (Blue Book)',
  'Subcontractor',
  'Material',
  'Attachment',
  'Bundle',
] as const;

/** One type per group; RESOURCE_GROUP_ORDER[i] is the heading for RESOURCE_GROUP_TYPES[i]. */
export const RESOURCE_GROUP_TYPES: ResourceType[] = ['EQUIPMENT', 'SUBCONTRACTOR', 'MATERIAL', 'ATTACHMENT', 'BUNDLE'];

export function resourceGroup(resource: { resourceType: ResourceType }): string {
  switch (resource.resourceType) {
    case 'EQUIPMENT':
      return 'Hired Equipment (Blue Book)';
    case 'ATTACHMENT':
      return 'Attachment';
    case 'SUBCONTRACTOR':
      return 'Subcontractor';
    case 'MATERIAL':
      return 'Material';
    case 'BUNDLE':
      return 'Bundle';
  }
}

/** Groups resources under their headings, preserving RESOURCE_GROUP_ORDER and dropping empties. */
export function groupResourcesByType<T extends { resourceType: ResourceType }>(
  resources: T[]
): Array<{ group: string; resources: T[] }> {
  return RESOURCE_GROUP_ORDER.map((group) => ({
    group,
    resources: resources.filter((r) => resourceGroup(r) === group),
  })).filter((g) => g.resources.length > 0);
}

export const RESOURCE_TYPE_OPTIONS = Object.entries(RESOURCE_TYPE_LABELS).map(([value, label]) => ({
  value: value as ResourceType,
  label,
}));

/** Canonical fuel class stored on VendorResource.vehicleType — keys the fuel surcharge lookup. */
export const RESOURCE_VEHICLE_TYPE_OPTIONS = ['On-Road', 'Off-Road', 'N/A'] as const;
export type ResourceVehicleType = (typeof RESOURCE_VEHICLE_TYPE_OPTIONS)[number];

export function resourceCarriesFuelSurcharge(resourceType: ResourceType): boolean {
  return resourceType === 'EQUIPMENT' || resourceType === 'ATTACHMENT' || resourceType === 'SUBCONTRACTOR';
}

/**
 * Units of Measure offered when creating or editing a vendor resource (M17c). A controlled list:
 * the server refuses anything else for a new value (RESOURCE_UNITS in
 * server/src/modules/vendor/vendor-resource.units.ts — keep the two in step). A legacy value a
 * resource already carries is still shown and may be kept.
 */
export const RESOURCE_UNIT_OPTIONS = [
  'hours',
  'day',
  'week',
  'month',
  'each',
  'dollars',
  'km',
  'rd km',
  'sh km',
  'litres',
  'm3',
  'tonne',
] as const;

/** M17b (Part 2): every new resource starts on "hours"; Units of Measure is always required. Mirrors the server. */
export const DEFAULT_UNIT_BY_TYPE: Record<ResourceType, (typeof RESOURCE_UNIT_OPTIONS)[number]> = {
  EQUIPMENT: 'hours',
  ATTACHMENT: 'hours',
  SUBCONTRACTOR: 'hours',
  MATERIAL: 'hours',
  BUNDLE: 'hours',
};

/**
 * Map a stored unit onto a dropdown value. A case-insensitive match uses the canonical
 * option; an unknown (legacy) value is kept so editing an existing resource does not blank it.
 */
export function canonicalizeResourceUnit(unit: string | null | undefined): string {
  if (!unit) return '';
  const match = RESOURCE_UNIT_OPTIONS.find((option) => option.toLowerCase() === unit.toLowerCase());
  return match ?? unit;
}

function compactUnit(unit: string | null | undefined): string {
  return (unit ?? '').trim().toLowerCase().replace(/[.\s]/g, '');
}

/** Collapse aliases (Hours/hr, each/ea) so a bundle can require one unit of measure. */
export function unitOfMeasureKey(unit: string | null | undefined): string | null {
  const compact = compactUnit(unit);
  if (!compact) return null;
  if (compact === 'hours' || compact === 'hour' || compact === 'hrs' || compact === 'hr') return 'hours';
  if (compact === 'each' || compact === 'ea' || compact === 'eac') return 'each';
  if (compact === 'day' || compact === 'days') return 'day';
  if (
    compact === 'litres'
    || compact === 'litre'
    || compact === 'liters'
    || compact === 'liter'
    || compact === 'l'
  ) {
    return 'litres';
  }
  return compact;
}

export function unitsOfMeasureMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = unitOfMeasureKey(left);
  const b = unitOfMeasureKey(right);
  return a != null && b != null && a === b;
}

export function resourceUnitSelectOptions(current?: string | null): string[] {
  const canonical = canonicalizeResourceUnit(current);
  if (!canonical || (RESOURCE_UNIT_OPTIONS as readonly string[]).includes(canonical)) {
    return [...RESOURCE_UNIT_OPTIONS];
  }
  return [...RESOURCE_UNIT_OPTIONS, canonical];
}

export const VENDOR_DOC_TYPE_LABELS: Record<VendorDocType, string> = {
  QUOTE: 'Quote',
  WCB: 'WCB Clearance',
  // Company-level cover. Distinct from a resource's vehicle insurance, which lives on
  // the resource (expiry + optional certificate) and never gates activation — the two
  // used to share the word "insurance" on the same screen.
  LIABILITY_INSURANCE: 'Liability Insurance',
  // Not compliance evidence: signed questionnaires, agreements, correspondence. Expiry is
  // optional for this type because most of them never expire - see EXPIRY_REQUIRED in
  // VendorDocumentModal, and computeCompliance on the server, which ignores this type.
  GENERAL: 'General Document',
  // Optional evidence for a rate-change request. Also ignored by computeCompliance.
  RATE_AGREEMENT: 'Rate Change Agreement',
};

export type VendorHistoryEntityType =
  | 'VENDOR'
  | 'RESOURCE'
  | 'RATE'
  | 'DOCUMENT'
  | 'SERVICE_AREA'
  | 'RESOURCE_AREA';

export interface VendorHistoryDetail {
  field: string;
  oldValue: string | number | null;
  newValue: string | number | null;
}

export interface VendorHistoryEntry {
  id: number;
  vendorId: number;
  resourceId: number | null;
  entityType: VendorHistoryEntityType | string;
  entityId: number | null;
  stage: string;
  summary: string;
  details: VendorHistoryDetail[] | null;
  userId: number | null;
  createdAt: string;
  user: { id: number; name: string; lastName: string } | null;
  resource: { id: number; name: string } | null;
}

export interface AreaRef {
  id: number;
  name: string;
  code?: string | null;
}

export interface UserRef {
  id: number;
  name: string;
  lastName: string;
}

/**
 * HE-M18. Computed server-side so every screen shows the same verdict, and so "missing"
 * and "expired" stay distinguishable — they read differently to a coordinator deciding
 * whether to authorise work.
 */
export interface ComplianceStatus {
  wcbExpiresAt: string | null;
  insuranceExpiresAt: string | null;
  wcbMissing: boolean;
  insuranceMissing: boolean;
  wcbExpired: boolean;
  insuranceExpired: boolean;
  hasIssue: boolean;
}

export interface VendorServiceAreaLink {
  id: number;
  vendorId: number;
  serviceAreaId: number;
  isActive: boolean;
  serviceArea: AreaRef;
}

export interface VendorDocument {
  id: number;
  vendorId: number;
  docType: VendorDocType;
  fileUrl: string;
  fileName: string;
  issuedAt?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  uploadedBy?: UserRef | null;
}

export interface ResourceRate {
  id: number;
  resourceId: number;
  serviceAreaId: number;
  serviceArea?: AreaRef;
  rate: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  status: RateStatus;
  requestedById?: number | null;
  requestedBy?: UserRef | null;
  approvedById?: number | null;
  approvedBy?: UserRef | null;
  approvedAt?: string | null;
  supersedesId?: number | null;
  supersedes?: { id: number; rate: number; effectiveFrom: string; effectiveTo?: string | null } | null;
  comments?: string | null;
  agreementDocumentId?: number | null;
  agreementDocument?: { id: number; fileName: string } | null;
  /** M4 (Part 2): the coordinator's choice per affected HE submission, for a backdated rate. */
  backdatedChoices?: BackdatedChoiceRecord[] | null;
  createdAt?: string;
  resource?: {
    id: number;
    name: string;
    resourceType: ResourceType;
    unit?: string | null;
    vendor?: { id: number; name: string };
  };
}

/** M4: what approving a back-dated rate would have changed. Warning only — nothing is re-priced. */
export interface RateImpactLine {
  formId: string;
  formName: string;
  formStatus: string;
  lineIndex: number;
  date: string;
  qty: number;
  chargedRate: number | null;
  difference: number;
}

/** M4 (Part 2): one Hired Equipment submission a backdated rate would reprice. */
export interface BackdatedSubmission {
  formId: string;
  /** HE reference, or "Draft". */
  reference: string;
  status: string;
  lineCount: number;
  currentAmount: number;
  newAmount: number;
  difference: number;
}

export type BackdatedChoice = 'update' | 'leave';

export interface BackdatedChoiceRecord extends BackdatedSubmission {
  choice: BackdatedChoice;
}

export interface BackdatedPreview {
  backdated: boolean;
  windowStart: string;
  windowEnd: string;
  submissions: BackdatedSubmission[];
}

export interface RateImpact {
  rateId: number;
  newRate: number;
  priorRate: number | null;
  unit: string | null;
  serviceAreaName: string | null;
  windowStart: string;
  windowEnd: string;
  backdated: boolean;
  lines: RateImpactLine[];
  totalDifference: number;
}

/** H7: an existing vendor whose name collides with, or is close to, another. */
export interface VendorMatch {
  id: number;
  name: string;
  legalName: string | null;
  status: VendorStatus;
  matchedOn: 'name' | 'legalName';
  exact: boolean;
}

/**
 * An API failure with the structured parts the vendor screens act on: field-level validation
 * messages (M16) and duplicate-name matches (H7). `message` is always safe to show.
 */
export class VendorApiError extends Error {
  status?: number;
  code?: string;
  fieldErrors?: Record<string, string>;
  matches?: VendorMatch[];

  constructor(message: string, extra: Partial<Pick<VendorApiError, 'status' | 'code' | 'fieldErrors' | 'matches'>> = {}) {
    super(message);
    this.name = 'VendorApiError';
    Object.assign(this, extra);
  }
}

export interface VendorResourceAreaLink {
  id: number;
  resourceId: number;
  serviceAreaId: number;
  isActive: boolean;
  serviceArea: AreaRef;
}

export interface VendorResource {
  id: number;
  vendorId: number;
  name: string;
  resourceType: ResourceType;
  unit?: string | null;
  isFixedPrice: boolean;
  isActive: boolean;
  makeAndModel?: string | null;
  overtimeEligible: boolean;
  comments?: string | null;
  /** The operator's own code (HT / HE / HX). Distinct from resourceType. */
  equipmentClassification?: string | null;
  /** On-Road / Off-Road / N/A — also drives the fuel surcharge lookup. */
  vehicleType?: string | null;
  /**
   * Per-machine vehicle insurance. Separate from the vendor-level WCB and liability cover:
   * this is flag-only and never blocks activation. Opt-in, because an attachment or a material
   * does not carry its own policy. The certificate is optional evidence for the unit.
   */
  requiresVehicleInsurance: boolean;
  vehicleInsuranceExpDate?: string | null;
  vehicleInsuranceFileUrl?: string | null;
  vehicleInsuranceFileName?: string | null;
  /** Server-computed; mirrors the vendor-level ComplianceStatus shape. */
  vehicleInsurance?: {
    required: boolean;
    missing: boolean;
    expired: boolean;
    hasIssue: boolean;
  };
  /** The vendor's own number for this unit, as it appears on their invoices. */
  customerUnitNumber?: string | null;
  vendor?: { id: number; name: string; status: VendorStatus };
  serviceAreas?: VendorResourceAreaLink[];
  rates?: ResourceRate[];
  bundleComponents?: Array<{
    id: number;
    componentResourceId: number;
    qty: number;
    componentResource: {
      id: number;
      name: string;
      resourceType: ResourceType;
      unit?: string | null;
      /** M27: the component's own displayed rates, for the bundle expander. */
      rates?: ResourceRate[];
    };
  }>;
  /**
   * Which existing task markup bucket a cost for this resource lands in, or null for a
   * fixed-price resource that carries no markup at all. Derived server-side from
   * resourceType — never entered by a user (HE-M16).
   */
  effortType?: string | null;
  _count?: { poLineItems: number };
}

export interface Vendor {
  id: number;
  name: string;
  legalName?: string | null;
  status: VendorStatus;
  contactEmail?: string | null;
  phoneNumber?: string | null;
  comments?: string | null;
  /** M16 registration fields. */
  gstBusinessNumber?: string | null;
  wcbAccountNumber?: string | null;
  address?: string | null;
  erpVendorId?: string | null;
  /** H7: why the vendor was saved despite a similar existing name. */
  duplicateOverrideReason?: string | null;
  /** Server-computed, PENDING vendors only (H7): exact and near name matches for the approver. */
  possibleMatches?: VendorMatch[];
  /** Server-computed (M14b): what still blocks activation, in words. Empty when it can be activated. */
  activationBlockers?: string[];
  /** Null for vendors imported from the legacy sheets (M2) — shown as "Legacy import". */
  legacySubcontractorMasterId?: number | null;
  createdById?: number | null;
  createdBy?: UserRef | null;
  approvedById?: number | null;
  approvedBy?: UserRef | null;
  approvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  serviceAreas: VendorServiceAreaLink[];
  documents: VendorDocument[];
  resources?: VendorResource[];
  compliance: ComplianceStatus;
  _count?: { resources: number; purchaseOrders: number };
}

export interface VendorQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  statuses?: VendorStatus[];
  serviceAreaIds?: number[];
  complianceIssuesOnly?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ResourceQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  /** M1: resource name only. */
  nameSearch?: string;
  vendorId?: number;
  resourceTypes?: ResourceType[];
  equipmentClassifications?: string[];
  vehicleTypes?: string[];
  serviceAreaIds?: number[];
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Write payloads, spelled out rather than derived from the read types.
 *
 * The two shapes genuinely differ: reading a bundle component returns the joined resource,
 * writing one sends only an id and a quantity. `Partial<VendorResource>` would drag the read
 * shape onto the write path and make a correct payload a type error.
 */
export interface VendorUpdatePayload {
  name?: string;
  legalName?: string | null;
  contactEmail?: string | null;
  phoneNumber?: string | null;
  comments?: string | null;
  gstBusinessNumber?: string | null;
  wcbAccountNumber?: string | null;
  address?: string | null;
  erpVendorId?: string | null;
  /** H7: required by the server when a similarly named vendor exists. */
  duplicateOverrideReason?: string | null;
  serviceAreaIds?: number[];
}

export interface VendorCreatePayload extends VendorUpdatePayload {
  name: string;
  serviceAreaIds: number[];
}

export interface ResourceUpdatePayload {
  name?: string;
  equipmentClassification?: string | null;
  vehicleType?: string | null;
  requiresVehicleInsurance?: boolean;
  vehicleInsuranceExpDate?: string | null;
  customerUnitNumber?: string | null;
  unit?: string | null;
  isFixedPrice?: boolean;
  makeAndModel?: string | null;
  overtimeEligible?: boolean;
  comments?: string | null;
  isActive?: boolean;
  serviceAreaIds?: number[];
  bundleComponents?: Array<{ componentResourceId: number; qty: number }>;
}

export interface Paginated<T> {
  data: T[];
  /** Resource catalogue only (M1): matching rows per type, ignoring the type filter. */
  groupCounts?: Partial<Record<ResourceType, number>>;
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/** COUNT-only pending queues used by the Approvals navbar badge. */
export interface PendingApprovalSummary {
  pendingVendors: number;
  pendingRates: number;
  total: number;
}

class VendorService {
  private baseUrl = `${BASE_URL}/vendors`;

  private handleError(error: any): Error {
    if (error.response) {
      const data = error.response.data ?? {};
      return new VendorApiError(data.message || data.error || 'An error occurred', {
        status: error.response.status,
        code: data.code,
        fieldErrors: data.fieldErrors,
        matches: data.matches,
      });
    } else if (error.request) {
      return new Error('No response from server');
    }
    return new Error(error.message || 'An error occurred');
  }

  async getVendorsPaginated(params: VendorQueryParams = {}): Promise<Paginated<Vendor>> {
    try {
      const q = new URLSearchParams();
      if (params.page) q.append('page', String(params.page));
      if (params.limit) q.append('limit', String(params.limit));
      if (params.search) q.append('search', params.search);
      if (params.statuses?.length) q.append('statuses', params.statuses.join(','));
      if (params.serviceAreaIds?.length) q.append('serviceAreaIds', params.serviceAreaIds.join(','));
      if (params.complianceIssuesOnly) q.append('complianceIssuesOnly', 'true');
      if (params.sortBy) q.append('sortBy', params.sortBy);
      if (params.sortOrder) q.append('sortOrder', params.sortOrder);
      const response = await axios.get(`${this.baseUrl}?${q.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAllVendors(
    params: { search?: string; status?: VendorStatus; serviceAreaId?: number } = {}
  ): Promise<Array<{ id: number; name: string; status: VendorStatus }>> {
    try {
      const q = new URLSearchParams();
      if (params.search) q.append('search', params.search);
      if (params.status) q.append('status', params.status);
      if (params.serviceAreaId !== undefined) q.append('serviceAreaId', String(params.serviceAreaId));
      const response = await axios.get(`${this.baseUrl}/all?${q.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVendorById(id: number): Promise<Vendor> {
    try {
      const response = await axios.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVendorHistory(id: number): Promise<VendorHistoryEntry[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/${id}/history`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createVendor(data: VendorCreatePayload): Promise<Vendor> {
    try {
      const response = await axios.post(this.baseUrl, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateVendor(id: number, data: VendorUpdatePayload): Promise<Vendor> {
    try {
      const response = await axios.put(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Mark an existing vendor as available in one or more of the caller's service areas.
   * Used when the vendor was found in another area and should not be created again.
   */
  async addVendorServiceAreas(id: number, serviceAreaIds: number[]): Promise<Vendor> {
    try {
      const response = await axios.post(`${this.baseUrl}/${id}/service-areas`, { serviceAreaIds });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async activateVendor(id: number): Promise<Vendor> {
    try {
      const response = await axios.post(`${this.baseUrl}/${id}/activate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deactivateVendor(id: number): Promise<Vendor> {
    try {
      const response = await axios.post(`${this.baseUrl}/${id}/deactivate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** V2-078: the Vendor Compliance report rows and their counts. */
  async getComplianceReport(
    filters: VendorComplianceFilters = {},
  ): Promise<{ rows: VendorComplianceRow[]; counts: VendorComplianceCounts }> {
    try {
      const q = vendorComplianceQuery(filters);
      const response = await axios.get(`${this.baseUrl}/compliance-report${q ? `?${q}` : ''}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** V2-078: the CSV download of the report with these filters. */
  complianceReportCsvUrl(filters: VendorComplianceFilters = {}): string {
    const q = vendorComplianceQuery(filters);
    return `${this.baseUrl}/compliance-report?format=csv${q ? `&${q}` : ''}`;
  }

  /** V2-078: the Home tiles – the report's counts with its defaults. */
  async getComplianceCounts(): Promise<VendorComplianceCounts> {
    try {
      const response = await axios.get(`${this.baseUrl}/compliance-counts`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async listPendingActivation(serviceAreaId?: number): Promise<Vendor[]> {
    try {
      const q = serviceAreaId !== undefined ? `?serviceAreaId=${serviceAreaId}` : '';
      const response = await axios.get(`${this.baseUrl}/pending-activation${q}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * COUNT-only pending queues for the Approvals navbar badge.
   * Prefer this over downloading the full pending lists just to read `.length`.
   */
  async getPendingSummary(serviceAreaId?: number): Promise<PendingApprovalSummary> {
    try {
      const q = serviceAreaId !== undefined ? `?serviceAreaId=${serviceAreaId}` : '';
      const response = await axios.get(`${this.baseUrl}/pending-summary${q}`);
      const data = response.data ?? {};
      const pendingVendors = Number(data.pendingVendors) || 0;
      const pendingRates = Number(data.pendingRates) || 0;
      const total = Number(data.total);
      return {
        pendingVendors,
        pendingRates,
        total: Number.isFinite(total) ? total : pendingVendors + pendingRates,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // --- resources ---------------------------------------------------------

  async getResourcesPaginated(params: ResourceQueryParams = {}): Promise<Paginated<VendorResource>> {
    try {
      const q = new URLSearchParams();
      if (params.page) q.append('page', String(params.page));
      if (params.limit) q.append('limit', String(params.limit));
      if (params.search) q.append('search', params.search);
      if (params.nameSearch) q.append('nameSearch', params.nameSearch);
      if (params.vendorId !== undefined) q.append('vendorId', String(params.vendorId));
      if (params.resourceTypes?.length) q.append('resourceTypes', params.resourceTypes.join(','));
      if (params.equipmentClassifications?.length) {
        q.append('equipmentClassifications', params.equipmentClassifications.join(','));
      }
      if (params.vehicleTypes?.length) q.append('vehicleTypes', params.vehicleTypes.join(','));
      if (params.serviceAreaIds?.length) q.append('serviceAreaIds', params.serviceAreaIds.join(','));
      if (params.isActive !== undefined) q.append('isActive', String(params.isActive));
      if (params.sortBy) q.append('sortBy', params.sortBy);
      if (params.sortOrder) q.append('sortOrder', params.sortOrder);
      const response = await axios.get(`${this.baseUrl}/resources?${q.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createResource(
    vendorId: number,
    data: {
      name: string;
      resourceType: ResourceType;
      unit?: string;
      isFixedPrice?: boolean;
      makeAndModel?: string | null;
      overtimeEligible?: boolean;
      comments?: string;
      vehicleType?: string | null;
      requiresVehicleInsurance?: boolean;
      vehicleInsuranceExpDate?: string | null;
      customerUnitNumber?: string | null;
      serviceAreaIds: number[];
      bundleComponents?: Array<{ componentResourceId: number; qty: number }>;
      /** M17d: submitted for approval with the resource; without one it is created Inactive. */
      firstRate?: { serviceAreaId: number; rate: number; effectiveFrom?: string; comments?: string };
    }
  ): Promise<VendorResource> {
    try {
      const response = await axios.post(`${this.baseUrl}/${vendorId}/resources`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateResource(
    resourceId: number,
    data: ResourceUpdatePayload
  ): Promise<VendorResource> {
    try {
      const response = await axios.put(`${this.baseUrl}/resources/${resourceId}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** HE-M17: in/out of service in one area, without discarding that area's rate history. */
  async setResourceAreaActive(
    resourceId: number,
    serviceAreaId: number,
    isActive: boolean
  ): Promise<VendorResourceAreaLink> {
    try {
      const response = await axios.patch(
        `${this.baseUrl}/resources/${resourceId}/service-areas/${serviceAreaId}`,
        { isActive }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteResource(resourceId: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/resources/${resourceId}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async uploadVehicleInsurance(resourceId: number, file: File): Promise<VendorResource> {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post(
        `${this.baseUrl}/resources/${resourceId}/vehicle-insurance`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async clearVehicleInsurance(resourceId: number): Promise<VendorResource> {
    try {
      const response = await axios.delete(
        `${this.baseUrl}/resources/${resourceId}/vehicle-insurance`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // --- rates -------------------------------------------------------------

  async listRatesForResource(resourceId: number): Promise<ResourceRate[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/resources/${resourceId}/rates`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async listPendingRates(serviceAreaId?: number): Promise<ResourceRate[]> {
    try {
      const q = serviceAreaId !== undefined ? `?serviceAreaId=${serviceAreaId}` : '';
      const response = await axios.get(`${this.baseUrl}/pending-rates${q}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Every rate is created PENDING — a first rate as well as a change (H6) — and takes effect
   * only once a different person approves it.
   */
  async upsertRate(
    resourceId: number,
    data: {
      serviceAreaId: number;
      rate: number;
      effectiveFrom?: string;
      effectiveTo?: string | null;
      comments?: string;
      agreementDocumentId?: number;
      /** M4: required for every affected submission when the rate is backdated. */
      backdatedChoices?: { formId: string; choice: BackdatedChoice }[];
    }
  ): Promise<ResourceRate> {
    try {
      const response = await axios.post(`${this.baseUrl}/resources/${resourceId}/rates`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Withdraw your own change still awaiting approval (M5). The server refuses anything else. */
  async deleteRate(rateId: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/rates/${rateId}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** M4 (Part 2): before saving, the HE submissions a backdated rate would reprice. */
  async previewBackdatedRate(
    resourceId: number,
    data: { serviceAreaId: number; rate: number; effectiveFrom: string; effectiveTo?: string | null }
  ): Promise<BackdatedPreview> {
    try {
      const response = await axios.post(`${this.baseUrl}/resources/${resourceId}/rates/backdated-preview`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** M4: lines dated inside a back-dated rate's window, with the dollar difference. */
  async getRateImpact(rateId: number): Promise<RateImpact> {
    try {
      const response = await axios.get(`${this.baseUrl}/rates/${rateId}/impact`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async approveRate(rateId: number): Promise<ResourceRate> {
    try {
      const response = await axios.post(`${this.baseUrl}/rates/${rateId}/approve`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async rejectRate(rateId: number, comments?: string): Promise<ResourceRate> {
    try {
      const response = await axios.post(`${this.baseUrl}/rates/${rateId}/reject`, { comments });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // --- documents ---------------------------------------------------------

  async listDocuments(vendorId: number): Promise<VendorDocument[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/${vendorId}/documents`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async addDocument(
    vendorId: number,
    data: { docType: VendorDocType; file: File; issuedAt?: string; expiresAt?: string }
  ): Promise<VendorDocument> {
    try {
      const form = new FormData();
      form.append('file', data.file);
      form.append('docType', data.docType);
      if (data.issuedAt) form.append('issuedAt', data.issuedAt);
      if (data.expiresAt) form.append('expiresAt', data.expiresAt);
      const response = await axios.post(`${this.baseUrl}/${vendorId}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteDocument(documentId: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/documents/${documentId}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Opens through the server's B2 proxy rather than the stored `fileUrl` - DRM-Operations
   * is a private bucket, so a browser hitting that URL directly gets B2's
   * "UnauthorizedAccess" error back instead of the file.
   */
  getDocumentDownloadUrl(documentId: number): string {
    return `${this.baseUrl}/documents/${documentId}/download`;
  }
}

export const vendorService = new VendorService();
export default vendorService;
